/**
 * LumvinWood — motion runtime.
 *
 * 1. Scroll reveal for [data-lw-reveal] / [data-lw-stagger].
 * 2. (Page-transition curtain removed 2026-09-03 — pages now load plain.)
 *
 * Everything is a no-op under prefers-reduced-motion, and the reveal layer
 * fails open: without JS the .lw-no-js class keeps all content visible.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- reveal */

  const revealSelector = '[data-lw-reveal], [data-lw-stagger]';
  let observer = null;

  /**
   * Reveal, with a hard safety net: if the transition never ticks (throttled
   * tab, backgrounded render, stalled compositor) the element is forced
   * visible inline. Content must never be left invisible by an animation.
   */
  const showNow = (el) => {
    el.classList.add('lw-in');
    setTimeout(() => {
      el.style.opacity = '1';
      el.style.transform = 'none';
      if (el.hasAttribute('data-lw-stagger')) {
        for (const child of el.children) {
          child.style.opacity = '1';
          child.style.transform = 'none';
        }
      }
    }, 1200);
  };

  const observe = (root = document) => {
    const targets = root.querySelectorAll?.(revealSelector);
    if (!targets || !targets.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      targets.forEach(showNow);
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            showNow(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px 18% 0px', threshold: 0.01 }
      );
    }

    targets.forEach((el) => {
      if (el.classList.contains('lw-in')) return;
      // Anything already in view on load reveals immediately, so the first
      // screen never waits for a scroll event.
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 1.15 && rect.bottom > -200) {
        showNow(el);
      } else {
        observer.observe(el);
      }
    });
  };

  const init = () => {
    document.documentElement.classList.remove('lw-no-js');
    observe(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Horizon re-renders sections over AJAX (filters, pagination, cart).
  document.addEventListener('shopify:section:load', (e) => {
    observe(e.target);
  });
  // Theme editor: stop watching the nodes of a section that is going away,
  // otherwise the observer keeps the detached subtree alive on every edit.
  document.addEventListener('shopify:section:unload', (e) => {
    if (!observer || !e.target?.querySelectorAll) return;
    e.target.querySelectorAll(revealSelector).forEach((el) => observer.unobserve(el));
  });
  const mo = new MutationObserver((records) => {
    for (const rec of records) {
      for (const node of rec.addedNodes) {
        if (node.nodeType !== 1) continue;
        observe(node);
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
